import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type DsrRequest,
  type ErasurePlan,
  type ListDataRequestsResult,
} from '@whinerp/client';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  InlineAction,
  Num,
  Panel,
  SectionLabel,
  Skeleton,
} from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';

/**
 * Setup > Data subject requests (F-147).
 *
 * The screen IS the confirmation. Everything else here is a list and two
 * buttons; the part that had to be built carefully is the dialog, because the
 * feature's own note says a vague confirmation on an irreversible action is a
 * compliance risk in itself.
 *
 * So the dialog enumerates every store with its row count, says which stores
 * are emptied and which are kept-but-unnamed - they are not the same promise -
 * and names the stores that survive with the reason each survives. Then it
 * hands that exact list back to the server, which refuses if anything about it
 * has moved since. A confirmation nobody can act against is decoration.
 */
export function PrivacyPanel(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const errorMessage = useErrorMessage();
  const client = useQueryClient();

  const [email, setEmail] = useState('');
  const [kind, setKind] = useState<'access' | 'erasure'>('access');
  const [confirming, setConfirming] = useState<{ request: DsrRequest; plan: ErasurePlan } | null>(
    null,
  );
  const [refusing, setRefusing] = useState<DsrRequest | null>(null);
  const [reason, setReason] = useState('');
  const [outcome, setOutcome] = useState<string | null>(null);

  const queue = useQuery<ListDataRequestsResult, ApiError>({
    queryKey: ['dsr-requests'],
    queryFn: () => api.listDataRequests({}),
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['dsr-requests'] });
  };

  const open = useMutation<DsrRequest, ApiError>({
    mutationFn: () => api.openDataRequest({ body: { kind, subjectEmail: email } }),
    onSuccess: () => {
      setEmail('');
      setOutcome(null);
      refresh();
    },
  });

  /* Reading the plan is a separate act from erasing, and it is the only way to
     reach the erase button. There is no path from the list to the erasure that
     skips the list of stores. */
  const review = useMutation<ErasurePlan, ApiError, DsrRequest>({
    mutationFn: (request) =>
      api.planErasure({ query: { subjectEmail: request.subjectEmail } }),
    onSuccess: (plan, request) => setConfirming({ request, plan }),
  });

  const erase = useMutation<DsrRequest, ApiError>({
    mutationFn: () =>
      api.completeErasureRequest({
        path: { requestId: confirming!.request.requestId },
        // The confirmation itself, counts and all, exactly as it was read.
        body: {
          acknowledged: confirming!.plan.stores.map((store) => ({
            table: store.table,
            count: store.count,
          })),
        },
      }),
    onSuccess: () => {
      setOutcome(
        t('privacy.erased', {
          values: { count: formatCount(confirming!.plan.stores.length, format) },
        }),
      );
      setConfirming(null);
      refresh();
    },
  });

  const sendExport = useMutation<{ request: DsrRequest }, ApiError, DsrRequest>({
    mutationFn: (request) =>
      api.completeAccessRequest({ path: { requestId: request.requestId } }),
    onSuccess: (result) => {
      const counts = (result.request.outcome?.['exported'] ?? {}) as Record<string, number>;
      const rows = Object.values(counts).reduce((sum, n) => sum + n, 0);
      setOutcome(
        t('privacy.exported', { values: { count: formatCount(rows, format) } }),
      );
      refresh();
    },
  });

  const refuse = useMutation<DsrRequest, ApiError>({
    mutationFn: () =>
      api.refuseDataRequest({ path: { requestId: refusing!.requestId }, body: { reason } }),
    onSuccess: () => {
      setRefusing(null);
      setReason('');
      refresh();
    },
  });

  // A skeleton means "still loading". Rendering one on error is a spinner that
  // never resolves.
  if (queue.isPending) return <Skeleton className="h-block-sm w-full" />;
  if (queue.isError) return <></>;

  const requests = queue.data.requests;
  const plan = confirming?.plan;

  return (
    <Panel data-region="privacy" id="privacy">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('privacy.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('privacy.body')}</p>

        <div className="flex flex-wrap items-end gap-2">
          <Field
            label={t('privacy.subjectEmail')}
            type="email"
            value={email}
            data-dsr-email
            onChange={(event) => setEmail(event.target.value)}
            className="min-w-[16rem] flex-1"
          />
          <div className="flex gap-2">
            <Button
              data-dsr-kind="access"
              intent={kind === 'access' ? 'primary' : 'secondary'}
              onClick={() => setKind('access')}
            >
              {t('privacy.kind.access')}
            </Button>
            <Button
              data-dsr-kind="erasure"
              intent={kind === 'erasure' ? 'primary' : 'secondary'}
              onClick={() => setKind('erasure')}
            >
              {t('privacy.kind.erasure')}
            </Button>
          </div>
          <Button
            data-dsr-open
            intent="primary"
            disabled={email.trim() === '' || open.isPending}
            onClick={() => open.mutate()}
          >
            {t('privacy.open')}
          </Button>
        </div>
        {open.isError ? (
          <p role="alert" className="text-meta text-danger">
            {errorMessage(open.error)}
          </p>
        ) : null}
        {outcome ? (
          <p data-dsr-outcome role="status" className="text-meta text-ink-meta">
            {outcome}
          </p>
        ) : null}

        {requests.length === 0 ? (
          <EmptyState
            data-dsr-empty
            illustration="ShieldCheck"
            title={t('privacy.queue')}
            body={t('privacy.empty')}
            primary={{ label: t('privacy.open'), icon: 'Plus', onClick: () => open.mutate() }}
          />
        ) : (
          <ul className="flex flex-col gap-2" data-dsr-queue>
            {requests.map((request) => (
              <li
                key={request.requestId}
                data-dsr-request={request.requestId}
                data-dsr-status={request.status}
                className="flex flex-wrap items-center gap-2 rounded-chip bg-sunken px-3 py-2"
              >
                <span className="flex-1 text-body text-ink">{request.subjectEmail}</span>
                <span className="text-meta text-ink-meta">
                  {t(
                    request.kind === 'access' ? 'privacy.kind.access' : 'privacy.kind.erasure',
                  )}
                </span>
                <span className="text-meta text-ink-meta" data-dsr-state>
                  {t(`privacy.status.${request.status}` as 'privacy.status.open')}
                </span>
                {/* An answered request keeps its evidence and offers no further
                    action: the record is the point, not the row. */}
                {request.status === 'open' ? (
                  <>
                    {request.kind === 'erasure' ? (
                      <InlineAction
                        data-dsr-review={request.requestId}
                        onClick={() => review.mutate(request)}
                      >
                        {t('privacy.review')}
                      </InlineAction>
                    ) : (
                      <InlineAction
                        data-dsr-export={request.requestId}
                        onClick={() => sendExport.mutate(request)}
                      >
                        {t('privacy.export')}
                      </InlineAction>
                    )}
                    <InlineAction
                      data-dsr-refuse={request.requestId}
                      onClick={() => {
                        setRefusing(request);
                        setReason('');
                      }}
                    >
                      {t('privacy.refuse')}
                    </InlineAction>
                  </>
                ) : null}
                {request.refusal ? (
                  <span data-dsr-refusal className="w-full text-meta text-ink-meta">
                    {request.refusal}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/*
        The confirmation.

        Every store, with its count and what will happen to it, then the stores
        that survive with the reason each one does. An approver shown eight
        stores and told nothing else would reasonably conclude there is no
        ninth, so the ninth is on screen.
      */}
      <ConfirmDialog
        open={confirming !== null}
        title={
          plan
            ? t('privacy.confirmTitle', {
                values: {
                  email: plan.subjectEmail,
                  count: formatCount(plan.stores.length, format),
                },
              })
            : ''
        }
        confirmLabel={
          plan
            ? t('privacy.erase', {
                values: { count: formatCount(plan.stores.length, format) },
              })
            : ''
        }
        confirmIntent="danger"
        labels={{ cancel: t('action.cancel') }}
        busy={erase.isPending}
        blockedReason={
          plan?.blocked ??
          (plan?.empty ? t('privacy.nothing') : undefined) ??
          (erase.isError ? errorMessage(erase.error) : undefined)
        }
        onCancel={() => setConfirming(null)}
        onConfirm={() => erase.mutate()}
        data-dsr-confirm
      >
        <div className="flex flex-col gap-3">
          <p className="text-body text-ink">{t('privacy.confirmBody')}</p>
          {plan?.alreadyErased ? (
            <p data-dsr-already className="text-meta text-ink-meta">
              {t('privacy.already')}
            </p>
          ) : null}
          <ul className="flex flex-col gap-1" data-dsr-stores>
            {plan?.stores.map((store) => (
              <li
                key={store.table}
                data-dsr-store={store.table}
                data-dsr-store-count={store.count}
                data-dsr-store-action={store.action}
                className="flex items-baseline justify-between gap-3 text-meta"
              >
                <span className="text-ink">{store.label}</span>
                <span className="text-ink-meta">
                  <Num>{formatCount(store.count, format)}</Num>{' '}
                  {t(
                    store.action === 'delete'
                      ? 'privacy.action.delete'
                      : 'privacy.action.anonymise',
                  )}
                </span>
              </li>
            ))}
          </ul>
          {plan && plan.retained.length > 0 ? (
            <div className="flex flex-col gap-1" data-dsr-retained>
              <SectionLabel>{t('privacy.retainedTitle')}</SectionLabel>
              {plan.retained.map((store) => (
                <p key={store.table} data-dsr-retained-store={store.table} className="text-meta text-ink-meta">
                  {store.label} · <Num>{formatCount(store.count, format)}</Num> — {store.reason}
                </p>
              ))}
            </div>
          ) : null}
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={refusing !== null}
        title={t('privacy.refuse')}
        confirmLabel={t('privacy.refuse')}
        confirmIntent="danger"
        labels={{ cancel: t('action.cancel') }}
        busy={refuse.isPending}
        blockedReason={refuse.isError ? errorMessage(refuse.error) : undefined}
        onCancel={() => setRefusing(null)}
        onConfirm={() => refuse.mutate()}
        data-dsr-refuse-dialog
      >
        <Field
          label={t('privacy.refuseReason')}
          value={reason}
          data-dsr-reason
          onChange={(event) => setReason(event.target.value)}
        />
      </ConfirmDialog>
    </Panel>
  );
}
